/**
 * Renders per-product Yotpo star ratings on product cards.
 * Uses Yotpo's public bottomline CDN API so each card gets its own score
 * (Shopify reviews metafields are empty on this store).
 */

const STAR_PATH =
  'M31.547 12a.848.848 0 00-.677-.577l-9.427-1.376-4.224-8.532a.847.847 0 00-1.516 0l-4.218 8.534-9.427 1.355a.847.847 0 00-.467 1.467l6.823 6.664-1.612 9.375a.847.847 0 001.23.893l8.428-4.434 8.432 4.432a.847.847 0 001.229-.894l-1.615-9.373 6.822-6.665a.845.845 0 00.214-.869z';

/** @type {Map<string, Promise<{ average: number, count: number }>>} */
const ratingCache = new Map();

/**
 * Stars are fetched and built only for cards near the viewport.
 *
 * Every card on a page carries one of these placeholders, and hydrating all of them at
 * once meant a request per product plus about thirteen nodes injected into each card
 * before the shopper had scrolled anywhere — on the home page that was two carousels'
 * worth of stars nobody had looked at yet.
 *
 * The margin is deliberately generous, and applies on all four sides: cards enter
 * vertically on scroll but horizontally when a carousel is dragged, and stars appearing
 * under a card that is already on screen is the thing worth avoiding.
 */
const NEAR_VIEWPORT_MARGIN = '600px';

/** Placeholders sit inside the card, so the card is what gets watched. See whenNearViewport. */
const CARD_ROOT_SELECTOR = 'product-card, .product-card, .resource-list__item';

/** @type {IntersectionObserver | null} */
let viewportObserver = null;

/** @type {WeakMap<Element, HTMLElement[]>} */
const deferredByAnchor = new WeakMap();

/**
 * @returns {IntersectionObserver | null} Null where IntersectionObserver is missing.
 */
function getViewportObserver() {
  if (viewportObserver) return viewportObserver;
  if (typeof IntersectionObserver === 'undefined') return null;

  viewportObserver = new IntersectionObserver(
    (entries) => {
      for (const entry of entries) {
        if (!entry.isIntersecting) continue;
        viewportObserver?.unobserve(entry.target);
        const waiting = deferredByAnchor.get(entry.target);
        deferredByAnchor.delete(entry.target);
        if (!waiting) continue;
        for (const el of waiting) {
          if (el.isConnected) hydrateCard(el);
        }
      }
    },
    { rootMargin: NEAR_VIEWPORT_MARGIN }
  );

  return viewportObserver;
}

/**
 * Hold a placeholder until its card comes near the viewport, then hydrate it.
 *
 * @param {HTMLElement} el
 */
function whenNearViewport(el) {
  const observer = getViewportObserver();

  // The placeholder carries `hidden` until it has stars, so it has no layout box and
  // would never report as intersecting. The card around it is what can be seen.
  const anchor = el.closest(CARD_ROOT_SELECTOR) || el.parentElement;

  if (!observer || !anchor) {
    hydrateCard(el);
    return;
  }

  // Marked so the repeated hydrateAll() sweeps below stop re-queueing the same cards.
  el.dataset.yotpoLoaded = 'deferred';

  const waiting = deferredByAnchor.get(anchor);
  if (waiting) {
    if (!waiting.includes(el)) waiting.push(el);
    return;
  }

  deferredByAnchor.set(anchor, [el]);
  observer.observe(anchor);
}

/** @type {string | null | undefined} */
let cachedAppKey;

/**
 * @returns {string | null}
 */
function discoverYotpoAppKey() {
  if (cachedAppKey !== undefined) return cachedAppKey;

  const win = /** @type {any} */ (window);
  const fromWindow =
    win.yotpoAppKey ||
    win.yotpo?.appKey ||
    win.yotpo?.apiKey ||
    win.yotpoWidgetsContainer?.appKey ||
    win.yotpoWidgetsContainer?.instance?.appKey;

  if (typeof fromWindow === 'string' && fromWindow.length > 0) {
    cachedAppKey = fromWindow;
    return cachedAppKey;
  }

  for (const script of document.querySelectorAll('script[src*="yotpo"]')) {
    const src = script.getAttribute('src') || '';
    const patterns = [
      /staticw2\.yotpo\.com\/([^/?#]+)/,
      /cdn-widgetsrepository\.yotpo\.com\/v1\/loader\/([^/?#]+)/,
      /cdn-static\.yotpo\.com\/[^/?#]+\/([^/?#]+)/,
      /[?&]app_key=([^&]+)/,
      /\/applications\/static\/([^/?#]+)/,
    ];

    for (const pattern of patterns) {
      const match = src.match(pattern);
      if (match?.[1] && match[1] !== 'widget.js' && match[1] !== 'v1' && match[1] !== 'v3') {
        cachedAppKey = decodeURIComponent(match[1]);
        return cachedAppKey;
      }
    }
  }

  const attrEl = document.querySelector('[data-yotpo-app-key], [data-appkey], .yotpo[data-appkey]');
  if (attrEl instanceof HTMLElement) {
    const key = attrEl.getAttribute('data-yotpo-app-key') || attrEl.getAttribute('data-appkey');
    if (key) {
      cachedAppKey = key;
      return cachedAppKey;
    }
  }

  cachedAppKey = null;
  return null;
}

/**
 * @param {string} appKey
 * @param {string} productId
 * @returns {Promise<{ average: number, count: number }>}
 */
function fetchBottomline(appKey, productId) {
  const cacheKey = `${appKey}:${productId}`;
  const cached = ratingCache.get(cacheKey);
  if (cached) return cached;

  const empty = { average: 0, count: 0 };

  const request = fetch(`https://api-cdn.yotpo.com/products/${encodeURIComponent(appKey)}/${encodeURIComponent(productId)}/bottomline`)
    .then(async (response) => {
      // 404 / empty product → still show 0 reviews
      if (!response.ok) return empty;
      const data = await response.json();
      const bottomline = data?.response?.bottomline;
      if (!bottomline) return empty;

      return {
        average: Number(bottomline.average_score) || 0,
        count: Number(bottomline.total_reviews) || 0,
      };
    })
    .catch(() => empty);

  ratingCache.set(cacheKey, request);
  return request;
}

/** Must match .product-card-yotpo__star and --filled in custom.css; the half star's gradient can't read them. */
const STAR_COLOR_FILLED = '#00263e';
const STAR_COLOR_EMPTY = '#d9d9d9';

/** Gradient ids must be unique in the document, and every card draws its own stars. */
let halfStarSeq = 0;

/**
 * Same thresholds as Yotpo's star-rating widget, so a card and its product page agree:
 * 4.9 is five full stars, 4.6 is four and a half, 4.2 is four.
 *
 * @param {number} star 1–5
 * @param {number} average
 * @returns {'full' | 'half' | 'empty'}
 */
function starFill(star, average) {
  if (star <= average + 0.25) return 'full';
  if (star <= average + 0.75) return 'half';
  return 'empty';
}

/**
 * @param {number} average
 * @param {number} count
 * @returns {string}
 */
function buildRatingHtml(average, count) {
  const stars = Array.from({ length: 5 }, (_, index) => {
    const star = index + 1;
    const fill = starFill(star, average);

    if (fill === 'full') {
      return `<svg class="product-card-yotpo__star product-card-yotpo__star--filled" viewBox="0 0 32 32" role="presentation"><path d="${STAR_PATH}"/></svg>`;
    }

    if (fill === 'half') {
      // Inline style, because the class rules in custom.css set `fill` and would beat a fill attribute.
      const gradientId = `product-card-yotpo-half-${++halfStarSeq}`;
      return `<svg class="product-card-yotpo__star product-card-yotpo__star--half" viewBox="0 0 32 32" role="presentation"><defs><linearGradient id="${gradientId}"><stop offset="51%" stop-color="${STAR_COLOR_FILLED}"/><stop offset="51%" stop-color="${STAR_COLOR_EMPTY}"/></linearGradient></defs><path d="${STAR_PATH}" style="fill: url(#${gradientId})"/></svg>`;
    }

    return `<svg class="product-card-yotpo__star" viewBox="0 0 32 32" role="presentation"><path d="${STAR_PATH}"/></svg>`;
  }).join('');

  return `
    <div class="product-card-yotpo__content">
      <div class="product-card-yotpo__stars" aria-hidden="true">${stars}</div>
      <span class="product-card-yotpo__count">(${count})</span>
    </div>
  `;
}

/**
 * @param {HTMLElement} el
 */
async function hydrateCard(el) {
  if (el.dataset.yotpoLoaded === 'true') return;

  const productId = el.getAttribute('data-product-id');
  if (!productId) return;

  el.dataset.yotpoLoaded = 'pending';

  let appKey = discoverYotpoAppKey();
  if (!appKey) {
    // Yotpo app embed may load after this script
    await new Promise((resolve) => setTimeout(resolve, 400));
    cachedAppKey = undefined;
    appKey = discoverYotpoAppKey();
  }

  if (!appKey) {
    el.dataset.yotpoLoaded = 'false';
    el.hidden = true;
    return;
  }

  const rating = await fetchBottomline(appKey, productId);
  // Card may have been replaced by morph while we were fetching
  if (!el.isConnected) return;

  const currentId = el.getAttribute('data-product-id');
  if (currentId !== productId) {
    el.dataset.yotpoLoaded = 'false';
    return hydrateCard(el);
  }

  el.innerHTML = buildRatingHtml(rating.average, rating.count);
  el.setAttribute(
    'aria-label',
    rating.count > 0
      ? `Rating of this product is ${rating.average} out of 5`
      : 'No reviews yet'
  );
  el.hidden = false;
  el.dataset.yotpoLoaded = 'true';
}

/**
 * @param {ParentNode} [root]
 */
function hydrateAll(root = document) {
  const nodes = root.querySelectorAll?.(
    '.product-card-yotpo[data-product-id]:not([data-yotpo-loaded="true"]):not([data-yotpo-loaded="pending"]):not([data-yotpo-loaded="deferred"])'
  );
  if (!nodes) return;

  nodes.forEach((node) => {
    if (node instanceof HTMLElement) whenNearViewport(node);
  });
}

function bindRefreshHooks() {
  // Capture: theme dispatches this with bubbles:false on the product-card
  document.addEventListener(
    'theme:product-card-refresh',
    (event) => {
      const card = event.target;
      if (!(card instanceof HTMLElement)) return;
      card.querySelectorAll('.product-card-yotpo').forEach((node) => {
        if (!(node instanceof HTMLElement)) return;
        delete node.dataset.yotpoLoaded;
        node.hidden = true;
        node.innerHTML = '';
        whenNearViewport(node);
      });
    },
    true
  );

  document.addEventListener('shopify:section:load', () => hydrateAll());

  const observer = new MutationObserver((mutations) => {
    for (const mutation of mutations) {
      for (const node of mutation.addedNodes) {
        if (!(node instanceof HTMLElement)) continue;
        if (node.matches?.('.product-card-yotpo[data-product-id]')) {
          delete node.dataset.yotpoLoaded;
          whenNearViewport(node);
        } else {
          hydrateAll(node);
        }
      }
    }
  });

  observer.observe(document.documentElement, { childList: true, subtree: true });
}

hydrateAll();
bindRefreshHooks();

// Retry once after Yotpo scripts typically finish loading
setTimeout(() => {
  cachedAppKey = undefined;
  document.querySelectorAll('.product-card-yotpo[data-yotpo-loaded="false"]').forEach((node) => {
    if (!(node instanceof HTMLElement)) return;
    delete node.dataset.yotpoLoaded;
    hydrateCard(node);
  });
  hydrateAll();
}, 1200);
